import api from '../../../services/api';

// Single home for the "Płynność" read model (Pulpit tiles and the Płynność tab).
// Backend: GET /api/reports/liquidity (liquidityReportService.js) and the typed
// cash balance /api/reports/liquidity/balances. OMS records no payments and
// has no bank feed: flows come from invoice due dates and fixed costs, the
// balance is typed by hand, so every screen says "bez pełnej informacji o zapłacie".

export interface LiquidityAging {
  notDue: number;
  days1to14: number;
  days15to30: number;
  over30: number;
}

export interface LiquiditySide {
  total: number;
  overdue: number;
  dueIn30: number;
  count: number;
  missingDueDateCount: number;
  aging: LiquidityAging;
}

export type LiquidityItemKind = 'SALES_INVOICE' | 'PURCHASE_INVOICE' | 'COST_INVOICE' | 'FIXED_COST';

/** One cash movement of the forecast: a document by its due date or a fixed cost on its payment day. */
export interface LiquidityItem {
  kind: LiquidityItemKind;
  flow: 'IN' | 'OUT';
  id: string;
  /** 'YYYY-MM-DD': due date, or the payment day of a fixed cost. */
  date: string;
  amount: number;
  documentNumber: string | null;
  counterparty: string | null;
  orderId: string | null;
  /** Purchase invoice: its ZD (the link when the invoice itself is out of reach). */
  supplierOrderId: string | null;
  issueDate: string | null;
  factoring: boolean;
  /** Fixed cost only. */
  name: string | null;
  category: string | null;
  month: string | null;
  /** Fixed cost from a template whose month is not materialized yet. */
  projected: boolean;
  /** Overdue receivables only. */
  daysOverdue: number | null;
  /** Fixed cost marked paid without a payment date (placed on its payment day). */
  paymentDateUnknown: boolean;
}

/** A week or month of the forecast. Balances are null without a typed balance. */
export interface LiquidityPeriod {
  start: string;
  end: string;
  openingBalance: number | null;
  inflows: number;
  outflows: number;
  /** Part of `outflows`. */
  fixedCosts: number;
  net: number;
  closingBalance: number | null;
  items: LiquidityItem[];
}

export interface LiquidityBalance {
  id: string;
  balanceDate: string;
  amount: number;
  note: string | null;
  createdByName: string | null;
  createdAt: string | null;
  ageDays: number;
  stale: boolean;
}

export interface LiquidityForecast {
  /** Cash on `asOf`: balance + bridge; null without a balance. */
  openingBalance: number | null;
  /** From the balance day to the day before `asOf` (settled items only). */
  bridge: { from: string; to: string | null; inflows: number; outflows: number; fixedCosts: number; net: number; items: LiquidityItem[] } | null;
  /** Term receivables past due: outside every balance. */
  overdue: { amount: number; count: number; items: LiquidityItem[] };
  /** Documents without a due date: in no balance. */
  undated: { count: number; inflows: number; outflows: number; items: LiquidityItem[] };
  weeks: LiquidityPeriod[];
  months: LiquidityPeriod[];
  weeksNet: number;
  monthsNet: number;
  lowestWeek: { start: string; end: string; closingBalance: number } | null;
  fixedCostPaymentDay: number;
  staleAfterDays: number;
}

export interface LiquidityReport {
  asOf: string;
  basis: string;
  receivables: LiquiditySide;
  payables: LiquiditySide;
  balance: LiquidityBalance | null;
  forecast: LiquidityForecast;
}

const record = (value: unknown): Record<string, any> => (value && typeof value === 'object' ? value as Record<string, any> : {});
const num = (value: unknown): number => (Number.isFinite(Number(value)) ? Number(value) : 0);
const text = (value: unknown, fallback = ''): string => (typeof value === 'string' ? value : fallback);
const list = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

function normalizeSide(value: unknown): LiquiditySide {
  const source = record(value);
  const aging = record(source.aging);
  return {
    total: num(source.total),
    overdue: num(source.overdue),
    dueIn30: num(source.dueIn30),
    count: num(source.count),
    missingDueDateCount: num(source.missingDueDateCount),
    aging: {
      notDue: num(aging.notDue),
      days1to14: num(aging.days1to14),
      days15to30: num(aging.days15to30),
      over30: num(aging.over30),
    },
  };
}

const nullableNum = (value: unknown): number | null => (value === null || value === undefined || value === '' || !Number.isFinite(Number(value)) ? null : Number(value));
const nullableText = (value: unknown): string | null => (typeof value === 'string' && value ? value : null);
const ITEM_KINDS: LiquidityItemKind[] = ['SALES_INVOICE', 'PURCHASE_INVOICE', 'COST_INVOICE', 'FIXED_COST'];

function normalizeItem(value: unknown): LiquidityItem {
  const source = record(value);
  return {
    kind: ITEM_KINDS.includes(source.kind) ? source.kind : 'PURCHASE_INVOICE',
    flow: source.flow === 'IN' ? 'IN' : 'OUT',
    id: text(source.id),
    date: text(source.date),
    amount: num(source.amount),
    documentNumber: nullableText(source.documentNumber),
    counterparty: nullableText(source.counterparty),
    orderId: nullableText(source.orderId),
    supplierOrderId: nullableText(source.supplierOrderId),
    issueDate: nullableText(source.issueDate),
    factoring: source.factoring === true,
    name: nullableText(source.name),
    category: nullableText(source.category),
    month: nullableText(source.month),
    projected: source.projected === true,
    daysOverdue: nullableNum(source.daysOverdue),
    paymentDateUnknown: source.paymentDateUnknown === true,
  };
}

function normalizePeriod(value: unknown): LiquidityPeriod {
  const source = record(value);
  return {
    start: text(source.start),
    end: text(source.end),
    openingBalance: nullableNum(source.openingBalance),
    inflows: num(source.inflows),
    outflows: num(source.outflows),
    fixedCosts: num(source.fixedCosts),
    net: num(source.net),
    closingBalance: nullableNum(source.closingBalance),
    items: list(source.items).map(normalizeItem),
  };
}

export function normalizeLiquidityBalance(value: unknown): LiquidityBalance | null {
  const source = record(value);
  if (!source.id || !source.balanceDate) return null;
  return {
    id: text(source.id),
    balanceDate: text(source.balanceDate),
    amount: num(source.amount),
    note: nullableText(source.note),
    createdByName: nullableText(source.createdByName),
    createdAt: nullableText(source.createdAt),
    ageDays: num(source.ageDays),
    stale: source.stale === true,
  };
}

export function normalizeLiquidityReport(value: unknown): LiquidityReport {
  const source = record(value);
  const forecast = record(source.forecast);
  const bridge = forecast.bridge ? record(forecast.bridge) : null;
  const overdue = record(forecast.overdue);
  const undated = record(forecast.undated);
  const lowest = forecast.lowestWeek ? record(forecast.lowestWeek) : null;
  return {
    asOf: text(source.asOf),
    basis: text(source.basis),
    receivables: normalizeSide(source.receivables),
    payables: normalizeSide(source.payables),
    balance: normalizeLiquidityBalance(source.balance),
    forecast: {
      openingBalance: nullableNum(forecast.openingBalance),
      bridge: bridge ? {
        from: text(bridge.from),
        to: nullableText(bridge.to),
        inflows: num(bridge.inflows),
        outflows: num(bridge.outflows),
        fixedCosts: num(bridge.fixedCosts),
        net: num(bridge.net),
        items: list(bridge.items).map(normalizeItem),
      } : null,
      overdue: { amount: num(overdue.amount), count: num(overdue.count), items: list(overdue.items).map(normalizeItem) },
      undated: { count: num(undated.count), inflows: num(undated.inflows), outflows: num(undated.outflows), items: list(undated.items).map(normalizeItem) },
      weeks: list(forecast.weeks).map(normalizePeriod),
      months: list(forecast.months).map(normalizePeriod),
      weeksNet: num(forecast.weeksNet),
      monthsNet: num(forecast.monthsNet),
      lowestWeek: lowest && nullableNum(lowest.closingBalance) !== null
        ? { start: text(lowest.start), end: text(lowest.end), closingBalance: num(lowest.closingBalance) }
        : null,
      fixedCostPaymentDay: num(forecast.fixedCostPaymentDay) || 10,
      staleAfterDays: num(forecast.staleAfterDays) || 7,
    },
  };
}

export async function fetchLiquidityReport(): Promise<LiquidityReport> {
  const response = await api.get('/reports/liquidity');
  return normalizeLiquidityReport(response.data);
}

export interface CashBalanceInput {
  balanceDate: string;
  amount: number;
  note?: string | null;
}

/** ADMIN only (backend canEnterCashBalance); a new append-only entry, never an edit. */
export async function enterCashBalance(input: CashBalanceInput): Promise<LiquidityBalance | null> {
  const response = await api.post('/reports/liquidity/balances', input);
  return normalizeLiquidityBalance({ ...record(response.data), ageDays: 0 });
}

export async function fetchCashBalances(limit = 10): Promise<LiquidityBalance[]> {
  const response = await api.get('/reports/liquidity/balances', { params: { limit } });
  return list(record(response.data).balances).map(normalizeLiquidityBalance).filter((row): row is LiquidityBalance => row !== null);
}
